import { Download } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageLoader } from "@/components/common/loader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  downloadPortalProforma,
  listProformas,
  type PortalProforma,
} from "@/features/portal/api";
import { formatPortalDate as fmt } from "@/features/portal/portal-format";
import { downloadBlob } from "@/lib/download";
import { formatCents } from "@/lib/utils";

/** `/portal/proposals` — proforma proposals the firm prepared and approved for
 * this client. Read-only; a quote, not a bill — but downloadable so the client
 * keeps a copy of the quoted pricing. */
export function PortalProformas() {
  const [rows, setRows] = useState<PortalProforma[] | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    listProformas()
      .then((list) => active && setRows(list))
      .catch(() => {
        if (!active) return;
        setRows((prev) => prev ?? []);
        toast.error("Couldn't load your proposals. Please refresh.");
      });
    return () => {
      active = false;
    };
  }, []);

  async function handleDownload(p: PortalProforma) {
    setDownloadingId(p.id);
    try {
      downloadBlob(`proposal-${p.invoiceNumber}.pdf`, await downloadPortalProforma(p.id));
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? "Download failed.");
    } finally {
      setDownloadingId(null);
    }
  }

  if (rows === null) return <PageLoader />;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 py-8">
      <h1 className="text-2xl font-semibold text-foreground">Proposals</h1>
      {rows.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground text-sm">
            You have no proposals yet. When your tax team sends one, it will appear here.
          </p>
        </Card>
      ) : (
        <Card className="gap-0 divide-y overflow-hidden p-0">
          {rows.map((p) => (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="font-medium text-foreground">Proposal {p.invoiceNumber}</span>
                {p.noteToClient && (
                  <span className="text-muted-foreground truncate text-sm">{p.noteToClient}</span>
                )}
                <span className="text-muted-foreground text-xs">Dated {fmt(p.invoiceDate)}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-heading text-lg font-semibold tabular-nums">
                  {formatCents(p.totalCents)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownload(p)}
                  disabled={downloadingId === p.id}
                >
                  <Download className="size-4" />
                  {downloadingId === p.id ? "Downloading…" : "Download"}
                </Button>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
